import GlobalFieldFormatMap from "./globalFieldFormatMap";
import { TableDataFormatter } from "@acl-services/sriracha-formatters/dist/Formatters";
import TimezoneOffset from "../../models/timezoneOffset/timezoneOffset";
import StringFormatter from "../../services/formatters/stringFormatter";

class GlobalValueFormatter {
  constructor() {
    this._timezoneOffset = new TimezoneOffset();
  }

  formatValue(value, fieldFormatIdentifier, fieldName, formatBlanks = false) {
    const fieldFormat = GlobalFieldFormatMap.getFieldFormat(fieldFormatIdentifier, fieldName);
    return this._formatValueWithFormat(value, fieldFormatIdentifier, fieldName, fieldFormat, formatBlanks);
  }

  normalizeValue(value, fieldType) {
    return TableDataFormatter.normalizeValue(value, fieldType);
  }

  setUtcOffset(utcOffsetSeconds) {
    this._timezoneOffset = new TimezoneOffset({ utcOffsetSeconds });
  }

  getTimezoneOffset() {
    return this._timezoneOffset;
  }

  getTimezoneOffsetForMetric(table, metric) {
    const metricConfig = metric.metricConfig();
    const timeFieldName = metricConfig.timeFieldName();
    const timeField = table.fieldByName(timeFieldName);
    if (!timeField || timeField.type() !== "datetime") return null;
    return this.getTimezoneOffset();
  }

  isHtmlFormatted(fieldFormatIdentifier, fieldName) {
    const fieldFormat = GlobalFieldFormatMap.getFieldFormat(fieldFormatIdentifier, fieldName);
    return (fieldFormat && fieldFormat.isHtml()) || false;
  }

  isUrlFormatted(fieldFormatIdentifier, fieldName) {
    const fieldFormat = GlobalFieldFormatMap.getFieldFormat(fieldFormatIdentifier, fieldName);
    return (fieldFormat && fieldFormat.isURL()) || false;
  }

  getUrlFormattedValue(value) {
    return StringFormatter.convertUrlLink(value);
  }

  _adjustPrecision(value, formatJson, fieldType, formatBlanks, utcOffsetMinutes, keepTrailingZeros) {
    let adjustedValue = value;

    if (typeof value === "bigint") {
      return value; // Return BigInt as-is
    }

    const [intPart, decPart] = String(adjustedValue).split(".");
    // If keepTrailingZeros is false, trailing zeros in the decimal part are removed.
    // This behavior is independent of preserveOriginalPrecision, which retains the original precision
    // of the number during formatting. Ensure API consumers are aware of this distinction.
    if (!keepTrailingZeros && decPart) {
      const cleanedDec = decPart.replace(/0+$/, "");
      if (cleanedDec) {
        adjustedValue = parseFloat(`${intPart}.${cleanedDec}`);
      } else if (Number.isFinite(Number(intPart)) && Number(intPart) <= Number.MAX_SAFE_INTEGER) {
        adjustedValue = parseInt(intPart, 10);
      }
    }

    const valueStr = String(adjustedValue);
    const dotIndex = valueStr.indexOf(".");

    if (dotIndex !== -1) {
      const actualPrecision = valueStr.length - dotIndex - 1;
      const adjustedFormat = {
        ...formatJson,
        precision: Math.min(actualPrecision, formatJson.precision != null ? formatJson.precision : actualPrecision),
      };

      delete adjustedFormat.preserveOriginalPrecision;

      return TableDataFormatter.formatValue(adjustedValue, fieldType, adjustedFormat, formatBlanks, utcOffsetMinutes);
    }

    const sanitizedFormatJson = { ...formatJson };
    delete sanitizedFormatJson.preserveOriginalPrecision;
    return TableDataFormatter.formatValue(
      adjustedValue,
      fieldType,
      sanitizedFormatJson,
      formatBlanks,
      utcOffsetMinutes
    );
  }

  // Allow for special cases that need to change format in a way before global
  // formatting
  _formatValueWithFormat(value, fieldFormatIdentifier, fieldName, fieldFormat, formatBlanks = false) {
    const fieldType = GlobalFieldFormatMap.getFieldType(fieldFormatIdentifier, fieldName);
    const fieldFormatJson = fieldFormat && fieldFormat.toJson();
    const utcOffsetMinutes = this._timezoneOffset.utcOffsetMinutes();

    if (fieldType === "numeric" && fieldFormatJson) {
      if (fieldFormatJson.isRaw) {
        /*
          Unformatted Value(isRaw) formatting option is removed for numeric fields as part of the ticket VRMRG-18.
          This logic is to maintain the backward compatibility for the fields that has Unformatted Value formatting option. Applying default formatting option 'Number' for the fields that has Unformatted Value
        */
        fieldFormatJson.isRaw = false;
        fieldFormatJson.thousandsDelimiter = true;
      }

      const { keepTrailingZeros, preserveOriginalPrecision } = fieldFormatJson;

      if (preserveOriginalPrecision && value != null && value !== "" && !isNaN(value)) {
        return this._adjustPrecision(
          value,
          fieldFormatJson,
          fieldType,
          formatBlanks,
          utcOffsetMinutes,
          keepTrailingZeros
        );
      }
    }

    return TableDataFormatter.formatValue(value, fieldType, fieldFormatJson, formatBlanks, utcOffsetMinutes);
  }
}

export default new GlobalValueFormatter();
